import { FormEvent, ReactNode } from 'react';

/**
 * Shared shell for /login and /register.
 *
 * Two reasons it lives in its own file:
 *   1. Mobile sizing is non-trivial — inputs need text-base
 *      (16px) so iOS Safari doesn't auto-zoom on focus, and
 *      touch targets need ≥44px height for an honest hit area.
 *   2. We share the styled card/header/button across both
 *      pages so they don't drift when one is updated and not
 *      the other.
 */

export function AuthCard({
  title,
  subtitle,
  children,
  footer,
  onSubmit,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <div className="mx-auto max-w-md">
      <div className="ec-surface p-6 sm:p-8">
        <header className="mb-6">
          <h1 className="font-display text-2xl font-semibold uppercase tracking-wide text-ec-face">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-ec-bone">{subtitle}</p>}
        </header>
        <form onSubmit={onSubmit} className="space-y-4">
          {children}
        </form>
      </div>
      {footer && <div className="mt-6 text-center text-sm text-ec-bone">{footer}</div>}
    </div>
  );
}

export function AuthField({
  label,
  type,
  value,
  onChange,
  autoComplete,
  required,
  minLength,
  maxLength,
  placeholder,
  trailing,
  inputMode,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  trailing?: ReactNode;
  inputMode?: 'text' | 'email' | 'numeric';
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-display text-sm font-medium uppercase tracking-wide text-ec-bone">{label}</span>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          placeholder={placeholder}
          inputMode={inputMode}
          // text-base = 16px keeps iOS Safari from auto-zooming
          // on focus. min-h-[44px] gives the touch target the
          // Apple-recommended size for a single-row control.
          className="block min-h-[44px] w-full rounded-md border border-ec-line bg-ec-ground px-3 py-2 text-base text-ec-face placeholder-ec-bone-dim focus:border-ec-brass focus:outline-none focus:ring-1 focus:ring-ec-brass"
        />
        {trailing && (
          <div className="absolute inset-y-0 right-2 flex items-center">{trailing}</div>
        )}
      </div>
    </label>
  );
}

export function AuthSubmit({
  busy,
  busyLabel,
  label,
}: {
  busy: boolean;
  busyLabel: string;
  label: string;
}) {
  return (
    <button
      type="submit"
      disabled={busy}
      // h-12 is the comfortable thumb-target size on phones.
      className="ec-btn ec-btn--primary h-12 w-full text-base"
    >
      {busy ? busyLabel : label}
    </button>
  );
}
